import React, { useMemo, useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { authService } from '../../services/authService';
import { getErrorMessage } from '../../utils/error';
import { useToast } from '../../components/common/Toast';
import { useLanguage } from '../../features/language/useLanguage';
import { Eye, EyeOff } from 'lucide-react';

const buildResetSchema = (t: (key: string, params?: Record<string, string | number>, fallback?: string) => string) =>
  z.object({
    newPassword: z.string().min(6, t('auth.validation.passwordMinLength')),
    confirmPassword: z.string().min(6, t('auth.validation.confirmPasswordMinLength')),
  }).refine((data) => data.newPassword === data.confirmPassword, {
    message: t('auth.validation.passwordMismatch'),
    path: ['confirmPassword'],
  });

type ResetFormData = z.infer<ReturnType<typeof buildResetSchema>>;

export const ResetPasswordPage: React.FC = () => {
  const [showPassword, setShowPassword] = useState(false);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { t } = useLanguage();
  const resetSchema = useMemo(() => buildResetSchema(t), [t]);
  const token = searchParams.get('token') || '';

  const {
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm<ResetFormData>({
    resolver: zodResolver(resetSchema),
    mode: 'onChange',
  });

  const onSubmit = async (data: ResetFormData) => {
    if (!token) {
      setError('root', { message: t('auth.validation.resetTokenInvalid') });
      return;
    }

    try {
      await authService.resetPassword(token, data.newPassword);
      showToast(t('auth.toast.passwordUpdated'), 'success');
      navigate('/login');
    } catch (error) {
      setError('root', { message: getErrorMessage(error) });
    }
  };

  return (
    <>
      <div className="mb-6">
        <h2 className="text-center text-3xl font-extrabold text-gray-900">
          {t('auth.resetPassword.title')}
        </h2>
        <p className="mt-2 text-center text-sm text-gray-600">
          {t('auth.resetPassword.subtitle')}
        </p>
      </div>

      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        {errors.root && (
          <div className="p-3 bg-red-100 border border-red-400 text-red-700 rounded" data-test-id="reset-password-error">
            {errors.root.message}
          </div>
        )}

        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div>
            <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700">
              {t('auth.resetPassword.newPassword')}
            </label>
              <div className="relative">
                <input
                  id="newPassword"
                  type={showPassword ? 'text' : 'password'}
                  data-test-id="reset-password-new-input"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-all"
                  {...register('newPassword', { deps: ['confirmPassword'] })}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.newPassword && (
                <p className="mt-2 text-sm text-red-600 font-medium">{errors.newPassword.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700">
                {t('auth.resetPassword.confirmNewPassword')}
              </label>
              <div className="relative">
                <input
                  id="confirmPassword"
                  type={showPassword ? 'text' : 'password'}
                  data-test-id="reset-password-confirm-input"
                  className="appearance-none block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm placeholder-gray-400 focus:outline-none focus:ring-primary-500 focus:border-primary-500 sm:text-sm transition-all"
                  {...register('confirmPassword')}
                />
                <button
                  type="button"
                  tabIndex={-1}
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                >
                  {showPassword ? <EyeOff size={16} /> : <Eye size={16} />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="mt-2 text-sm text-red-600 font-medium">{errors.confirmPassword.message}</p>
              )}
            </div>
          </div>

        <div>
          <button
            type="submit"
            disabled={isSubmitting}
            data-test-id="reset-password-submit-button"
            className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 transition-all font-bold"
          >
            {t('auth.resetPassword.updatePassword')}
          </button>
        </div>
      </form>

      <div className="mt-6 text-center text-sm text-gray-600">
        <Link to="/login" className="font-medium text-primary-600 hover:text-primary-500" data-test-id="reset-password-login-link">
          {t('auth.resetPassword.backToSignIn')}
        </Link>
      </div>
    </>
  );
};
